import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { AI_SATEI_URL, COMPANY } from '@/constants/company';

const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://clearcorp.info';

const aiSateiJsonLd = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebPage',
      '@id': `${siteUrl}/ai-satei#webpage`,
      url: `${siteUrl}/ai-satei`,
      name: 'AI査定｜名古屋不動産の売却・買取査定｜株式会社クリアコーポレーション',
      description: '名古屋16区の不動産AI査定。物件価格の目安を無料確認。売却・買取のご相談は株式会社クリアコーポレーションへ。秘密厳守・全国対応。',
      inLanguage: 'ja',
    },
    {
      '@type': 'Service',
      '@id': `${siteUrl}/ai-satei#service`,
      name: '不動産AI査定サービス',
      description: '最新の取引データをもとにAIが瞬時に分析。約60秒・完全無料・個人情報不要で不動産の目安価格を確認できます。',
      provider: {
        '@type': 'RealEstateAgent',
        name: '株式会社クリアコーポレーション',
        url: siteUrl,
        telephone: '052-325-5812',
      },
      areaServed: '名古屋市全16区・全国',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'JPY',
        description: '完全無料のAI査定サービス',
      },
    },
  ],
};

const steps = [
  {
    number: '01',
    title: '物件情報を入力',
    desc: '住所・物件種別・広さなど基本情報を入力します。約60秒で完了します。',
    icon: 'ri-home-4-line',
  },
  {
    number: '02',
    title: 'AIが瞬時に分析',
    desc: '最新の取引データをもとにAIが自動で価格を算出します。',
    icon: 'ri-cpu-line',
  },
  {
    number: '03',
    title: '査定結果を確認',
    desc: '目安価格をすぐに確認できます。正式な査定は専門スタッフが対応します。',
    icon: 'ri-file-chart-line',
  },
  {
    number: '04',
    title: '専門家に相談（任意）',
    desc: '結果を見てご不明な点があれば、電話・メールでいつでもご相談ください。',
    icon: 'ri-customer-service-2-line',
  },
];

const faqs = [
  {
    q: 'AI査定は無料ですか？',
    a: 'はい、完全無料でご利用いただけます。費用は一切かかりません。',
  },
  {
    q: '個人情報は必要ですか？',
    a: 'AI査定のみであれば、氏名・連絡先の入力は不要です。物件情報だけで目安価格を確認できます。',
  },
  {
    q: 'AI査定の結果は正式な査定価格ですか？',
    a: 'AI査定はあくまで目安です。正式な査定価格は、専門スタッフが現地調査・詳細確認を行った上でご案内します。',
  },
  {
    q: '査定後に売却を強制されますか？',
    a: 'いいえ、一切ありません。査定結果を確認するだけでも大歓迎です。売却のご判断はお客様のペースで構いません。',
  },
  {
    q: 'どんな物件でも査定できますか？',
    a: 'マンション・戸建・土地など幅広く対応しています。まずはお試しください。',
  },
];

export default function AiSateiPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    tel: '',
    propertyType: '',
    address: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const params = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => params.append(key, value));
      await fetch('https://readdy.ai/api/form/d77krhcggtbbtt19rlhg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      // silent
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <title>名古屋西区不動産AI査定｜売却・買取の無料査定｜株式会社クリアコーポレーション</title>
      <meta name="description" content="名古屋西区の不動産AI査定。名古屋西区をはじめ16区の物件価格の目安を無料確認。名古屋西区不動産の売却・買取のご相談は株式会社クリアコーポレーションへ。秘密厳守・全国対応。" />
      <meta name="keywords" content="名古屋西区 不動産AI査定, 名古屋西区 不動産査定, 名古屋 不動産 AI査定, 不動産 無料査定, クリアコーポレーション" />
      <script type="application/ld+json">{JSON.stringify(aiSateiJsonLd)}</script>
      <Header />

      {/* Hero */}
      <section className="relative pt-24 pb-20 md:pt-32 md:pb-28 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ab36f8cb6491cad5732ce39ad6341619.jpg"
            alt="AI査定"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1A2B4A]/90 via-[#1A2B4A]/70 to-[#1A2B4A]/40" />
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 text-center">
          <span className="inline-block border border-white/40 text-white/80 text-xs px-4 py-1.5 rounded-full mb-6 tracking-widest">
            無料 · 約60秒 · 個人情報不要
          </span>
          <h1 className="text-white text-3xl md:text-5xl font-bold leading-tight mb-6">
            AI査定で<br className="md:hidden" />物件価格の<br />
            <span className="text-[#4A90E2]">目安を今すぐ確認</span>
          </h1>
          <p className="text-white/80 text-base md:text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
            最新の取引データをもとにAIが瞬時に分析。<br className="hidden md:block" />
            まずは目安を知るだけでも、次のステップが見えてきます。
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={AI_SATEI_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-white text-[#1A2B4A] px-10 py-4 rounded-full font-bold text-base whitespace-nowrap hover:bg-gray-100 transition-all duration-200 cursor-pointer shadow-lg"
            >
              <i className="ri-arrow-right-circle-line mr-2 text-[#4A90E2]" />
              AI査定をはじめる（約60秒）
            </a>

          </div>
          <p className="text-white/50 text-xs mt-5">営業時間 {COMPANY.hours}　TEL {COMPANY.tel}</p>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 bg-[#F8F9FB]">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#4A90E2] text-sm font-medium tracking-widest uppercase mb-3">Features</p>
            <h2 className="text-[#1A2B4A] text-2xl md:text-3xl font-bold">AI査定の2つの特長</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-2xl mx-auto">
            {[
              { icon: 'ri-timer-line', title: '約60秒で完了', desc: '住所と物件種別を入力するだけ。難しい操作は一切不要です。' },
              { icon: 'ri-money-cny-circle-line', title: '完全無料', desc: '査定費用は一切かかりません。売却しなくても問題ありません。' },
            ].map((item) => (
              <div key={item.title} className="bg-white rounded-2xl p-8 text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-[#EEF3FB] rounded-full mx-auto mb-5">
                  <i className={`${item.icon} text-2xl text-[#4A90E2]`} />
                </div>
                <h3 className="text-[#1A2B4A] font-bold text-lg mb-3">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="text-center mb-14">
            <p className="text-[#4A90E2] text-sm font-medium tracking-widest uppercase mb-3">How it works</p>
            <h2 className="text-[#1A2B4A] text-2xl md:text-3xl font-bold">査定・相談の流れ</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {steps.map((step, idx) => (
              <div key={step.number} className="relative">
                {idx < steps.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-full w-full h-px bg-gray-200 z-0" style={{ width: 'calc(100% - 2rem)', left: 'calc(50% + 2rem)' }} />
                )}
                <div className="bg-[#F8F9FB] rounded-2xl p-6 text-center relative z-10">
                  <div className="w-12 h-12 flex items-center justify-center bg-[#1A2B4A] rounded-full mx-auto mb-4">
                    <i className={`${step.icon} text-xl text-white`} />
                  </div>
                  <span className="text-[#4A90E2] text-xs font-bold tracking-widest">{step.number}</span>
                  <h3 className="text-[#1A2B4A] font-bold text-base mt-1 mb-2">{step.title}</h3>
                  <p className="text-gray-500 text-xs leading-relaxed">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12">
            <a
              href={AI_SATEI_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-[#1A2B4A] text-white px-12 py-4 rounded-full font-bold text-base whitespace-nowrap hover:bg-[#243d6b] transition-colors duration-200 cursor-pointer"
            >
              <i className="ri-arrow-right-circle-line mr-2" />
              AI査定をはじめる（約60秒）
            </a>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 md:py-24 bg-[#F8F9FB]">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#4A90E2] text-sm font-medium tracking-widest uppercase mb-3">Contact</p>
            <h2 className="text-[#1A2B4A] text-2xl md:text-3xl font-bold mb-4">
              査定・ご相談フォーム
            </h2>
            <p className="text-gray-500 text-sm leading-relaxed">
              「まだ売るか決めていない」「相続の件で聞きたい」など、<br className="hidden md:block" />
              どんな段階でもお気軽にご連絡ください。
            </p>
          </div>

          {submitted ? (
            <div className="bg-white rounded-2xl p-12 text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-[#EEF3FB] rounded-full mx-auto mb-6">
                <i className="ri-check-line text-3xl text-[#4A90E2]" />
              </div>
              <h3 className="text-[#1A2B4A] font-bold text-xl mb-3">送信が完了しました</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-8">
                お問い合わせありがとうございます。<br />
                担当者より営業時間内（{COMPANY.hours}）にご連絡いたします。
              </p>
              <Link
                to="/"
                className="inline-block border border-[#1A2B4A] text-[#1A2B4A] px-8 py-3 rounded-full text-sm font-medium hover:bg-[#F0F4FA] transition-colors duration-200 cursor-pointer"
              >
                トップページへ戻る
              </Link>
            </div>
          ) : (
            <form
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-8 md:p-10"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-[#1A2B4A] text-sm font-medium mb-2">
                    お名前 <span className="text-red-400 text-xs">必須</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="山田 太郎"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#4A90E2] transition-colors duration-200"
                  />
                </div>
                <div>
                  <label className="block text-[#1A2B4A] text-sm font-medium mb-2">
                    電話番号
                  </label>
                  <input
                    type="tel"
                    name="tel"
                    value={formData.tel}
                    onChange={handleChange}
                    placeholder="052-000-0000"
                    className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#4A90E2] transition-colors duration-200"
                  />
                </div>
              </div>
              <div className="mb-6">
                <label className="block text-[#1A2B4A] text-sm font-medium mb-2">
                  メールアドレス <span className="text-red-400 text-xs">必須</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="example@email.com"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#4A90E2] transition-colors duration-200"
                />
              </div>
              <div className="mb-6">
                <label className="block text-[#1A2B4A] text-sm font-medium mb-2">
                  物件種別
                </label>
                <select
                  name="propertyType"
                  value={formData.propertyType}
                  onChange={handleChange}
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#4A90E2] transition-colors duration-200 bg-white"
                >
                  <option value="">選択してください</option>
                  <option value="マンション">マンション</option>
                  <option value="戸建">戸建</option>
                  <option value="土地">土地</option>
                  <option value="その他">その他</option>
                </select>
              </div>
              <div className="mb-6">
                <label className="block text-[#1A2B4A] text-sm font-medium mb-2">
                  物件の所在地（おおよそでOK）
                </label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="例：名古屋市西区"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#4A90E2] transition-colors duration-200"
                />
              </div>
              <div className="mb-8">
                <label className="block text-[#1A2B4A] text-sm font-medium mb-2">
                  ご相談内容・ご質問
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  maxLength={500}
                  placeholder="「相続した物件の査定をしたい」「売却を検討中だが、まず話を聞きたい」など、お気軽にどうぞ。"
                  className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm text-gray-700 focus:outline-none focus:border-[#4A90E2] transition-colors duration-200 resize-none"
                />
                <p className="text-gray-400 text-xs mt-1 text-right">{formData.message.length}/500文字</p>
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#1A2B4A] text-white py-4 rounded-full font-bold text-base whitespace-nowrap hover:bg-[#243d6b] transition-colors duration-200 cursor-pointer disabled:opacity-60"
              >
                {submitting ? '送信中...' : '送信する'}
              </button>
              <p className="text-gray-400 text-xs text-center mt-4">
                送信後、営業時間内（{COMPANY.hours}）に担当者よりご連絡いたします。
              </p>
            </form>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <div className="text-center mb-12">
            <p className="text-[#4A90E2] text-sm font-medium tracking-widest uppercase mb-3">FAQ</p>
            <h2 className="text-[#1A2B4A] text-2xl md:text-3xl font-bold">よくある質問</h2>
          </div>
          <div className="space-y-3">
            {faqs.map((faq, idx) => (
              <div key={idx} className="border border-gray-100 rounded-xl overflow-hidden">
                <button
                  onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                  className="w-full flex items-center justify-between px-6 py-5 text-left cursor-pointer hover:bg-[#F8F9FB] transition-colors duration-200"
                >
                  <span className="text-[#1A2B4A] font-medium text-sm pr-4">{faq.q}</span>
                  <i className={`text-[#4A90E2] text-lg flex-shrink-0 transition-transform duration-200 ${openFaq === idx ? 'ri-subtract-line' : 'ri-add-line'}`} />
                </button>
                {openFaq === idx && (
                  <div className="px-6 pb-5">
                    <p className="text-gray-600 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/faq"
              className="text-[#4A90E2] text-sm hover:underline cursor-pointer"
            >
              すべてのFAQを見る →
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
